import React, { useEffect, useRef, useState } from "react";
import { api, rupiah } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Loader2, CheckCircle2, FileText, Image as ImageIcon, MessageCircle } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");
const verifyUrl = (id) => `${window.location.origin}/verify/${id}`;

export default function ParentHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [struk, setStruk] = useState(null);
  const [busy, setBusy] = useState("");
  const receiptRef = useRef(null);

  useEffect(() => {
    api.get("/parent/riwayat-bills").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const capture = async () => {
    const canvas = await html2canvas(receiptRef.current, { scale: 2, backgroundColor: "#ffffff" });
    return canvas;
  };

  const savePdf = async () => {
    setBusy("pdf");
    try {
      const canvas = await capture();
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(img, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`struk_${struk.student}_${struk.label}.pdf`);
    } catch { toast.error("Gagal membuat struk PDF"); } finally { setBusy(""); }
  };

  const saveJpg = async () => {
    setBusy("jpg");
    try {
      const canvas = await capture();
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/jpeg", 0.95);
      a.download = `struk_${struk.student}_${struk.label}.jpg`;
      a.click();
    } catch { toast.error("Gagal membuat struk JPG"); } finally { setBusy(""); }
  };

  const sendWA = async () => {
    setBusy("wa");
    const text = `*Struk Pembayaran - Pondok Pesantren Al-Khoir*\nSantri: ${struk.student}\nTagihan: ${struk.label}\nJumlah Tagihan: ${rupiah(struk.total_amount)}\nDibayar: ${rupiah(struk.paid_amount)}\nStatus: ${struk.status === "paid" ? "LUNAS" : "DIBAYAR SEBAGIAN"}\nTanggal: ${fmt(struk.paid_at)}\nVerifikasi keaslian: ${verifyUrl(struk.id)}`;
    try {
      const canvas = await capture();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.95));
      const file = new File([blob], "struk.jpg", { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
        setBusy(""); return;
      }
    } catch { /* fallback below */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    setBusy("");
  };

  return (
    <div>
      <PageHeader title="Riwayat Pembayaran" subtitle="Tagihan yang sudah dibayar (lunas) maupun dibayar sebagian (cicilan). Unduh struk sebagai bukti bayar." />

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : rows.length === 0 ? (
          <Empty><Receipt className="mx-auto mb-2" /> Belum ada pembayaran.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nama Santri</TableHead>
                <TableHead>Nama Tagihan</TableHead>
                <TableHead className="text-right">Jumlah Tagihan</TableHead>
                <TableHead className="text-right">Dibayar</TableHead>
                <TableHead>Tanggal Bayar</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Struk</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`riwayat-row-${b.id}`}>
                  <TableCell className="font-medium text-stone-800">{b.student}</TableCell>
                  <TableCell>
                    {b.label}
                    {b.manual_by && <span className="block text-xs text-stone-400">Manual oleh {b.manual_by}</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(b.total_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(b.paid_amount)}</TableCell>
                  <TableCell className="text-sm">{fmt(b.paid_at)}</TableCell>
                  <TableCell>
                    {b.status === "paid"
                      ? <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Lunas</span>
                      : <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">Dibayar Sebagian</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setStruk(b)} data-testid={`struk-${b.id}`} className="rounded-lg">
                      <Receipt size={14} className="mr-1" /> Struk
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!struk} onOpenChange={(o) => !o && setStruk(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Struk Pembayaran</DialogTitle></DialogHeader>
          {struk && (
            <>
              <div ref={receiptRef} className="bg-white p-5 border border-stone-200 rounded-xl">
                <div className="text-center border-b border-dashed border-stone-300 pb-3">
                  <p className="font-heading font-bold text-stone-900">PONDOK PESANTREN AL-KHOIR</p>
                  <p className="text-xs text-stone-500">Struk Pembayaran Tagihan</p>
                </div>
                <div className="py-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-stone-500">Santri</span><span className="font-medium">{struk.student}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Tagihan</span><span className="font-medium text-right">{struk.label}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Tanggal</span><span>{fmt(struk.paid_at)}</span></div>
                  {struk.manual_by && <div className="flex justify-between"><span className="text-stone-500">Metode</span><span>Manual ({struk.manual_by})</span></div>}
                </div>
                <div className="border-t border-dashed border-stone-300 pt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-stone-500">Jumlah Tagihan</span><span className="font-mono">{rupiah(struk.total_amount)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Dibayar</span><span className="font-mono font-bold text-emerald-700">{rupiah(struk.paid_amount)}</span></div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-stone-500">Status</span>
                    <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${struk.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {struk.status === "paid" ? "LUNAS" : "DIBAYAR SEBAGIAN"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-center mt-4 pt-3 border-t border-dashed border-stone-300">
                  <QRCodeCanvas value={verifyUrl(struk.id)} size={92} level="M" includeMargin />
                  <p className="text-center text-[10px] text-stone-400 mt-2">Pindai QR untuk verifikasi keaslian struk oleh petugas.</p>
                  <p className="text-center text-[10px] text-stone-400">Terima kasih. Simpan struk ini sebagai bukti pembayaran.</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <Button onClick={savePdf} disabled={busy === "pdf"} data-testid="struk-pdf" className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
                  {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <><FileText size={16} className="mr-1" /> PDF</>}
                </Button>
                <Button onClick={saveJpg} disabled={busy === "jpg"} data-testid="struk-jpg" variant="outline" className="rounded-xl">
                  {busy === "jpg" ? <Loader2 size={16} className="animate-spin" /> : <><ImageIcon size={16} className="mr-1" /> JPG</>}
                </Button>
                <Button onClick={sendWA} disabled={busy === "wa"} data-testid="struk-wa" className="rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white">
                  {busy === "wa" ? <Loader2 size={16} className="animate-spin" /> : <><MessageCircle size={16} className="mr-1" /> WA</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
